import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { styled } from "@iventis/styles";
import * as d3 from "d3";
import { ScaleTime } from "d3";
import { combineLatest } from "rxjs";
import { filter } from "rxjs/operators";
import { Session, SessionComponent } from "./types";
import { CalendarCursor, SESSION_RECT_PADDING } from "./calendar-context";
import { GroupScale } from "./group-axis";
import { useCursor, useGroupScale, usePropsStream, useTimeScale, useUpdateCursor, useZoomStream } from "./hooks";

// returns contrasting text colour based on the luminosity of background colour
const getContrastTextColour = (bgColour: string): string => {
    const rgb = d3.rgb(d3.color(bgColour));
    // Luminance formula from RGB. Photometric/digital ITU BT.709:
    const luma = 0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b;
    return luma > 165 ? "#000" : "#fff";
};

interface SessionBriefProps {
    session: Session;
    sessionComponent: SessionComponent;
    onClick?: (session: Session, event: PointerEvent) => void;
    onContextClick?: (session: Session, event: PointerEvent) => void;
}

export const SessionBrief: React.FC<SessionBriefProps> = ({ session, onClick, sessionComponent, onContextClick }) => {
    const ref = useRef<SVGGElement>();
    const bgColour = session.colour.fill;
    const textColour = useMemo(() => getContrastTextColour(bgColour), [bgColour]);
    const props$ = usePropsStream();
    const timeScale$ = useTimeScale();
    const groupScale$ = useGroupScale();
    const zoomScale$ = useZoomStream();
    const cursor$ = useCursor();
    const updateCursor = useUpdateCursor();

    const clickHandler = useCallback(
        (event: React.MouseEvent<SVGGElement, Event>) => {
            onClick?.(session, event.nativeEvent as PointerEvent);
            event.stopPropagation();
        },
        [session, onClick]
    );

    const contextClickHandler = useCallback(
        (event) => {
            onContextClick?.(session, event.nativeEvent);
            event.preventDefault();
            event.stopPropagation();
        },
        [session, onContextClick]
    );

    useEffect(() => {
        const sub = combineLatest([timeScale$.pipe(filter<ScaleTime<number, number, never>>(Boolean)), groupScale$.pipe(filter<GroupScale>(Boolean))]).subscribe(
            ([timeScale, groupScale]) => {
                const start = session.sessionStart;
                const end = session.sessionEnd;
                const svg = d3.select(ref.current);
                const xRange = groupScale.getGroupCoords(session.groupId);
                const y = Math.floor(timeScale(start));
                const width = Math.max(xRange[1] - xRange[0] - 2 * SESSION_RECT_PADDING, 0);
                const height = Math.abs(timeScale(end) - y);
                svg.attr("transform", `translate(${xRange[0]}, ${y})`);
                svg.selectChildren().attr("width", Math.floor(width)).attr("height", Math.floor(height));
            }
        );

        return () => sub.unsubscribe();
    }, [timeScale$, groupScale$, props$, zoomScale$, session]);

    useEffect(() => {
        const sub = cursor$.subscribe((cursor) => {
            d3.select(ref.current).select("foreignObject").style("cursor", cursor);
        });
        return () => sub.unsubscribe();
    }, [cursor$]);

    const SessionRendererComponent = sessionComponent;

    // when the mouse if over the SessionContainer
    const handleMouseOver = useCallback(() => {
        updateCursor(CalendarCursor.POINTER);
    }, [updateCursor]);

    // when the mouse leaves the SessionContainer
    const handleMouseLeave = useCallback(() => {
        updateCursor(CalendarCursor.GRAB);
    }, [updateCursor]);

    return (
        <SessionContainer
            data-cy="calendar-event"
            ref={ref}
            onClick={clickHandler}
            onContextMenu={contextClickHandler}
            onMouseOver={handleMouseOver}
            onMouseLeave={handleMouseLeave}
        >
            <rect fill={bgColour} stroke={session.colour.outline} rx={4} x={SESSION_RECT_PADDING} y={0} />
            <foreignObject x={SESSION_RECT_PADDING} y={0}>
                <SessionRendererComponent session={session} contrastTextColour={textColour} />
            </foreignObject>
        </SessionContainer>
    );
};

const SessionContainer = styled.g`
    &:hover {
        rect {
            filter: url(#dropshadow);
            stroke-width: 2;
        }
    }
`;
